import { listText } from '@/services/my-work/my-work-merge.utils'

/**
 * A run that ends without closing the Claude report it set on its PR leaves the PR saying Claude is still working, and
 * on a merge-gate repo holds the merge. The fleet closes such a report for an ended run, as the run's own sweep would,
 * when someone follows its Stop this run link or picks Close from the run's ⋮ menu.
 */

export interface FleetRunPendingReports {
  runId: string
  /** The run's PR, `<repo>#<id>`. */
  item: string
  repo: string
  pr: number
  commit: string
  /** The reports closed, or on a check the ones that would be ("Claude review"). */
  closed: { key: string; name: string }[]
  /** Set when the PR's merge gate was set to ask for a Re-review. */
  gate?: 'FAILED'
  /** True on a read that wrote nothing. */
  check?: boolean
  asOf: string
}

export type FleetRunReportsCheck =
  | { status: 'loading' }
  | { status: 'ready'; reports: FleetRunPendingReports }
  | { status: 'failed'; why: string }

/** How long a run's pending reports are reused before its ⋮ menu asks the fleet again. */
export const FLEET_RUN_REPORTS_CHECK_TTL_MS = 30_000

export const FLEET_RUN_REPORTS_HELP = 'The run ended without closing it, so the PR still shows Claude working on it.'

function namesOf(reports: Pick<FleetRunPendingReports, 'closed'>): string {
  return listText(reports.closed.map(report => report.name))
}

/** The ⋮ menu's choice for the reports an ended run left pending, or null when it left none. */
export function closeReportsTitle(reports: Pick<FleetRunPendingReports, 'closed'>): string | null {
  return reports.closed.length ? `Close its pending ${namesOf(reports)}` : null
}

/** What closing did, in one or two sentences; null when there was nothing pending to close. */
export function closedReportsText(reports: Pick<FleetRunPendingReports, 'closed' | 'item' | 'gate'>): string | null {
  if (!reports.closed.length) return null
  const one = reports.closed.length === 1
  const closed = `Its pending ${namesOf(reports)} on ${reports.item} ${one ? 'is' : 'are'} now closed, so the PR no longer shows ${one ? 'it' : 'them'} running.`
  return reports.gate ? `${closed} Its merge check now asks for a Re-review.` : closed
}

/** What an agent reading the page learns of a run's pending reports: whether the menu has asked, and what is left. */
export function pendingReportsSummary(check: FleetRunReportsCheck | undefined) {
  if (!check) return null
  if (check.status !== 'ready') return { status: check.status }
  return { status: check.status, item: check.reports.item, pending: check.reports.closed.map(report => report.name) }
}
